import React, { Suspense, lazy, useEffect } from 'react';
import { Outlet, Route, Routes, Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { Box, CircularProgress } from '@mui/material';

import ProtectedRoute from '../auth/ProtectedRoute';
import AuthTokenAttacher from '../auth/AuthTokenAttacher';
import { useCurrentUser } from '../auth/useCurrentUser';

// Eager imports: landing + tenant shell are on the critical path for every
// first paint. Keeping them lazy costs an extra network round-trip before
// the app can render anything meaningful.
import SelectTenant from '../pages/SelectTenant/SelectTenant';
import TenantLayout from '../pages/Layout/Layout';
import NotFound from '../pages/NotFound/NotFound';
import AdminLayout from '../pages/Admin/AdminLayout';
import AdminHome from '../pages/Admin/AdminHome';
import AccessDenied from '../pages/AccessDenied/AccessDenied';
// Admin Round-2 surfaces — eager (the admin bundle is already super-admin-
// gated, and lazy-loading would just add an extra spinner on internal nav).
import GlobalCatalogLanding from '../pages/Admin/globals/GlobalCatalogLanding';
import TasksList from '../pages/Admin/globals/tasks/TasksList';
import TaskEdit from '../pages/Admin/globals/tasks/TaskEdit';
import CohortsList from '../pages/Admin/globals/cohorts/CohortsList';
import CohortEdit from '../pages/Admin/globals/cohorts/CohortEdit';
import SweepstakesList from '../pages/Admin/globals/sweepstakes/SweepstakesList';
import SweepstakesEdit from '../pages/Admin/globals/sweepstakes/SweepstakesEdit';
import ToSList from '../pages/Admin/globals/terms-of-service/ToSList';
import ToSEdit from '../pages/Admin/globals/terms-of-service/ToSEdit';
import QuestionnairesList from '../pages/Admin/globals/questionnaires/QuestionnairesList';
import QuestionnaireEdit from '../pages/Admin/globals/questionnaires/QuestionnaireEdit';
import CollectionsList from '../pages/Admin/globals/collections/CollectionsList';
import CollectionEdit from '../pages/Admin/globals/collections/CollectionEdit';
import MappingsList from '../pages/Admin/globals/plan-cohort-purse/MappingsList';
import MappingEdit from '../pages/Admin/globals/plan-cohort-purse/MappingEdit';
import CatalogsLanding from '../pages/Admin/catalogs/CatalogsLanding';
import TaskTypes from '../pages/Admin/catalogs/TaskTypes';
import TaskCategories from '../pages/Admin/catalogs/TaskCategories';
import TaskRewardTypes from '../pages/Admin/catalogs/TaskRewardTypes';
import WalletTypes from '../pages/Admin/catalogs/WalletTypes';
import NotificationChannels from '../pages/Admin/catalogs/NotificationChannels';
import NotificationCategories from '../pages/Admin/catalogs/NotificationCategories';
import ComponentTypes from '../pages/Admin/catalogs/ComponentTypes';
import OnboardingComponents from '../pages/Admin/catalogs/OnboardingComponents';
import CanonicalFields from '../pages/Admin/catalogs/CanonicalFields';
import CohortAttributeDefs from '../pages/Admin/catalogs/CohortAttributeDefs';
import UsersAdminPage from '../pages/Admin/users/UsersAdminPage';
// R7 — admin-view route-moves. Four pages migrated from the tenant app
// (FieldDefinitions, Scripts, RBAC, SecretDefaults/Audit) into /admin/*.
// Tenant-side pages were deleted this round; the /api/* backend mounts
// remain for one release for backward compat (see server/src/index.ts).
import FieldDefinitionsAdminPage from '../pages/Admin/fieldDefinitions/FieldDefinitionsAdminPage';
import AdminScriptsPage from '../pages/Admin/adminScripts/AdminScriptsPage';
import SecretDefaultsAuditAdminPage from '../pages/Admin/secretDefaultsAudit/SecretDefaultsAuditAdminPage';
import CustomerDefaultsAdminPage from '../pages/Admin/customerDefaults/CustomerDefaultsAdminPage';
import ImportExportPage from '../pages/Admin/ImportExport/ImportExportPage';
// WAVE G — per-tenant authoring-environment overview map.
import EditingEnvMap from '../pages/Admin/EditingEnvMap';

// Storyblok Visual Editor preview — PUBLIC route, sits outside AuthedShell.
// Storyblok iframes this anonymously when a content editor opens a story.
import StoryblokPreview from '../pages/StoryblokPreview/StoryblokPreview';

import RouteErrorBoundary from './RouteErrorBoundary';
import RequirePermission from './RequirePermission';
import RequireStoryblokMode from './RequireStoryblokMode';
import RequireActionsDisplayMode from './RequireActionsDisplayMode';
import LandingRedirect from './LandingRedirect';
import { paths } from './paths';
import { visibleTenantRoutes } from './domains';
import { colors } from '../theme/tokens';
import { firstGrantedSurfacePath } from '../auth/permissionCatalog';

// Storyblok HTML field plugin (GrapesJS visual editor) — PUBLIC, lazy-loaded so
// the heavy GrapesJS bundle only ships when this route is opened. Storyblok
// iframes it as a custom field editor; must sit outside AuthedShell.
const VisualEditorPlugin = lazy(() => import('../pages/VisualEditorPlugin/VisualEditorPlugin'));

const RouteFallback: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
    <CircularProgress sx={{ color: colors.brand }} />
  </Box>
);

/**
 * AuthedShell composes the two guards that every authenticated URL needs:
 * ProtectedRoute (login gate) + AuthTokenAttacher (bearer-token plumbing).
 * Pulled into an element prop on a route so public URLs (like /404 catch-all
 * cases or future /env-error, /callback pages) can sit outside it.
 */
const AuthedShell: React.FC = () => (
  <ProtectedRoute>
    <AuthTokenAttacher>
      <AppAccessRedirect />
      <LandingRedirect />
      <Outlet />
    </AuthTokenAttacher>
  </ProtectedRoute>
);

/**
 * Side-effect component: when `/api/me` reports an app-access denial,
 * route the user to `/access-denied`. Sits just inside AuthedShell so it
 * runs only for logged-in users, and renders nothing — it's a pure
 * navigation effect.
 *
 * When the denial clears (user refetches /api/me from the Access Denied
 * page), we intentionally do NOT navigate back automatically. The user's
 * "Try again" click produces a query refetch; once it succeeds the normal
 * AuthedShell tree re-renders without this redirect firing, and any link
 * they follow (or the landing page they refresh to) resolves naturally.
 */
const AppAccessRedirect: React.FC = () => {
  const { noAppAccessReason } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (noAppAccessReason && location.pathname !== paths.accessDenied()) {
      navigate(paths.accessDenied(), { replace: true });
    }
  }, [noAppAccessReason, location.pathname, navigate]);

  return null;
};

/**
 * Tenant index redirect (`/tenants/:tenantCode`) — replaces the old
 * hard-coded `<Navigate to="dashboard">`. Flag off: `dashboard`'s legacy
 * shim is always `true`, so this degenerates to the same target. Flag on: a
 * v2 role may not include `dashboard`, so this sends the user to the first
 * surface they actually hold instead of a dead end — or, for a
 * zero-permission role, to `/access-denied` with `state.reason:
 * 'no_surfaces'` so that page can explain the (rare, intentional) zero-
 * surface state instead of the generic app-access copy.
 */
const TenantIndexRedirect: React.FC = () => {
  const { tenantCode } = useParams<{ tenantCode: string }>();
  const currentUser = useCurrentUser();

  if (!tenantCode) return null;
  const target = firstGrantedSurfacePath(currentUser, tenantCode);
  if (target === paths.accessDenied()) {
    return <Navigate to={target} state={{ reason: 'no_surfaces' }} replace />;
  }
  return <Navigate to={target} replace />;
};

const AppRoutes: React.FC = () => {
  const tenantRoutes = visibleTenantRoutes();

  return (
    <Routes>
      {/* Authenticated surface ──────────────────────────────────── */}
      <Route element={<AuthedShell />}>
        <Route path="/" element={<SelectTenant />} />
        <Route path="/customers/:customerCode/sponsors" element={<SelectTenant />} />
        <Route
          path="/customers/:customerCode/sponsors/:sponsorCode/tenants"
          element={<SelectTenant />}
        />

        {/* Access-denied landing — reached when `/api/me` returns 403.
            Sits inside AuthedShell so Auth0 still gates reaching it, but
            outside AdminLayout / TenantLayout so users don't see broken
            chrome. AppAccessRedirect routes affected users here. */}
        <Route path="/access-denied" element={<AccessDenied />} />

        {/*
          Admin view surface. Declared BEFORE `/tenants/:tenantCode/*` so
          the `/admin` literal wins over any tenant code collision.
          AdminLayout itself gates on `(isSuperAdmin && adminViewEnabled)`
          and bounces everyone else to `/` — unauthenticated users never
          see this because AuthedShell above wraps both branches.
        */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminHome />} />
          {/* Global catalog landing — picker for all 8 global entities.
              Lets a super-admin see row counts at a glance and jump into
              any list. Individual list routes below remain unchanged. */}
          <Route path="global-catalog" element={<GlobalCatalogLanding />} />
          <Route path="global-catalog/tasks" element={<TasksList />} />
          <Route path="global-catalog/tasks/:code" element={<TaskEdit />} />
          {/* Adventures moved off globals — now tenant-scoped (see
              AdventuresList at pages/Actions/AdventuresList + mig 146). */}
          <Route path="global-catalog/cohorts" element={<CohortsList />} />
          <Route path="global-catalog/cohorts/:code" element={<CohortEdit />} />
          <Route path="global-catalog/sweepstakes" element={<SweepstakesList />} />
          <Route path="global-catalog/sweepstakes/:code" element={<SweepstakesEdit />} />
          <Route path="global-catalog/terms-of-service" element={<ToSList />} />
          <Route path="global-catalog/terms-of-service/:code" element={<ToSEdit />} />
          {/* R6 adds questionnaires, collections, plan-cohort-purse mappings. */}
          <Route path="global-catalog/questionnaires" element={<QuestionnairesList />} />
          <Route path="global-catalog/questionnaires/:code" element={<QuestionnaireEdit />} />
          <Route path="global-catalog/collections" element={<CollectionsList />} />
          <Route path="global-catalog/collections/:code" element={<CollectionEdit />} />
          <Route path="global-catalog/plan-cohort-purse" element={<MappingsList />} />
          <Route path="global-catalog/plan-cohort-purse/:code" element={<MappingEdit />} />
          {/* Reference catalogs — all 10 now live (R6 adds canonical-fields + cohort-attribute-defs). */}
          <Route path="catalogs" element={<CatalogsLanding />} />
          <Route path="catalogs/task-types" element={<TaskTypes />} />
          <Route path="catalogs/task-categories" element={<TaskCategories />} />
          <Route path="catalogs/task-reward-types" element={<TaskRewardTypes />} />
          <Route path="catalogs/wallet-types" element={<WalletTypes />} />
          <Route path="catalogs/notification-channels" element={<NotificationChannels />} />
          <Route path="catalogs/notification-categories" element={<NotificationCategories />} />
          <Route path="catalogs/component-types" element={<ComponentTypes />} />
          <Route path="catalogs/onboarding-components" element={<OnboardingComponents />} />
          <Route path="catalogs/canonical-fields" element={<CanonicalFields />} />
          <Route path="catalogs/cohort-attribute-defs" element={<CohortAttributeDefs />} />
          {/* R6 — Users admin (tabs: Users | Roles). */}
          <Route path="users" element={<UsersAdminPage />} />
          {/* R7 — four admin-view surfaces moved from tenant app. */}
          <Route path="field-definitions" element={<FieldDefinitionsAdminPage />} />
          <Route path="scripts" element={<AdminScriptsPage />} />
          {/* RBAC_V2 (decisions §17): the dead RBAC console is removed —
              redirect any old bookmark/link straight to the Roles tab of
              the real Users admin page. */}
          <Route path="rbac" element={<Navigate to="/admin/users?tab=roles" replace />} />
          <Route path="secret-defaults-audit" element={<SecretDefaultsAuditAdminPage />} />
          {/* Per-customer auth_config + secret defaults (super-admin; env-local via the env dropdown). */}
          <Route path="customer-defaults" element={<CustomerDefaultsAdminPage />} />
          {/* R7 — Import/Export (unified ZIP envelope for admin globals + catalogs). */}
          <Route path="import-export" element={<ImportExportPage />} />
          {/* WAVE G — per-tenant authoring-environment overview (read-only). */}
          <Route path="editing-env-map" element={<EditingEnvMap />} />
        </Route>

        <Route path="/tenants/:tenantCode" element={<TenantLayout />}>
          <Route index element={<TenantIndexRedirect />} />
          {tenantRoutes.map((r) => (
            <Route
              key={r.path}
              path={r.path}
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<RouteFallback />}>
                    <RequirePermission role={r.role} permission={r.permission} permissionAnyOf={r.permissionAnyOf}>
                      <RequireStoryblokMode
                        storyblok={r.storyblok}
                        flexSurface={!!r.sidebar?.alsoIn?.ignoreStoryblokGate}
                      >
                        <RequireActionsDisplayMode actionsSurface={r.actionsSurface}>
                          {r.element}
                        </RequireActionsDisplayMode>
                      </RequireStoryblokMode>
                    </RequirePermission>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
          ))}
          {/* Tenant-scoped 404 — preserves layout chrome so user can navigate */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>

      {/* Storyblok Visual Editor preview — PUBLIC.
          Storyblok iframes /storyblok/<full_slug>?_storyblok=… anonymously
          when an editor opens any story. Must sit outside AuthedShell
          (no Auth0 gate) so the iframe doesn't bounce to the login page. */}
      <Route path="/storyblok/*" element={<StoryblokPreview />} />

      {/* Storyblok HTML field plugin — PUBLIC. Storyblok iframes this as the
          custom editor for an HTML Body field. Lazy + Suspense so GrapesJS
          loads only here. Outside AuthedShell so the iframe isn't auth-gated. */}
      <Route
        path="/html/visual-editor"
        element={
          <Suspense fallback={<RouteFallback />}>
            <VisualEditorPlugin />
          </Suspense>
        }
      />

      {/* Public catch-all — reachable without auth (e.g. typo'd URL before login) */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
